import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import Webcam from "react-webcam";
import { Box, Chip, Grid, styled } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CameraSharpIcon from "@mui/icons-material/CameraSharp";
import NoPhotographySharpIcon from "@mui/icons-material/NoPhotographySharp";
import SaveIcon from "@mui/icons-material/Save";
import { Modal } from "../../molecules/Modal";
import Button from "../../atoms/Button";
import PhotoCard from "../../molecules/UploadedPhotosCard/PhotoCard";
import { H5, SubText } from "../../atoms/typography";
import ImageCaptureOverlay from "../ImageCaptureModal/ImageCaptureOverlay";
import LivePhotoInstructions from "../ImageCaptureModal/LivePhotoInstructions";
import ImageCaptureCornerSvg, { IMAGE_CAPTURE_CORNER_POSITION, } from "../ImageCaptureModal/ImageCaptureCornerSvg";
const IssueChip = styled(Chip) `
  border-radius: 20px;
  font-weight: ${({ selected }) => (selected ? 600 : 400)};
  border-color: ${({ selected, theme }) => selected ? theme.palette.warning.main : theme.palette.divider};
  border-width: ${({ selected }) => (selected ? "2px" : "1px")};
  color: ${({ selected, theme }) => selected ? theme.palette.warning.dark : "inherit"};
`;
const PhotoPlaceholder = styled(Box) `
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 160px;
  border: 1px dashed ${({ theme }) => theme.palette.divider};
  border-radius: 4px;
  cursor: pointer;
  background-color: ${({ theme }) => theme.palette.action.hover};
  &:hover {
    background-color: ${({ theme }) => theme.palette.action.selected};
  }
`;
const instructions = "Position your item in the center of the frame. Reduce clutter and make sure you have good lighting.";
const IssueReportModal = ({ isOpen, issues, onSubmit, onCancel, onClose, title = "What's the problem?", maxPhotos = 5, }) => {
    const [selectedIssue, setSelectedIssue] = useState(null);
    const [images, setImages] = useState({});
    const [isTakingPhoto, setIsTakingPhoto] = useState(false);
    const [isCameraAvailable, setIsCameraAvailable] = useState(false);
    const cameraRef = useRef(null);
    const getCameraAvailability = async () => {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasCamera = devices.some((device) => device.kind === "videoinput");
        const cameraPermissions = await navigator.permissions.query({
            name: "camera",
        });
        if (cameraPermissions.state === "granted" &&
            !isCameraAvailable &&
            hasCamera) {
            setIsCameraAvailable(true);
        }
        else if (cameraPermissions.state === "prompt") {
            if (isCameraAvailable) {
                setIsCameraAvailable(false);
            }
            try {
                await navigator.mediaDevices.getUserMedia({
                    video: true,
                    audio: false,
                });
                setIsCameraAvailable(hasCamera);
            }
            catch {
                setIsCameraAvailable(false);
            }
        }
    };
    useEffect(() => {
        if (isOpen) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            getCameraAvailability();
        }
    }, [isOpen, cameraRef]);
    const selectedOption = issues.find((issue) => issue.code === selectedIssue);
    const imageEntries = Object.entries(images);
    const reset = () => {
        setSelectedIssue(null);
        setImages({});
        setIsTakingPhoto(false);
    };
    const handleCancel = () => {
        reset();
        onCancel();
    };
    const handleClose = () => {
        reset();
        (onClose ?? onCancel)();
    };
    const handleSubmit = async () => {
        if (!selectedIssue)
            return;
        await onSubmit(selectedIssue, images);
        reset();
    };
    const handleTakeImage = useCallback(() => {
        if (!cameraRef.current)
            return;
        const image = cameraRef.current.getScreenshot();
        if (!image)
            return;
        setImages((prev) => {
            const nextIndex = Object.keys(prev).length + 1;
            return { ...prev, [`photo_${nextIndex}`]: image };
        });
        setIsTakingPhoto(false);
    }, []);
    const handleRemoveImage = (key) => {
        setImages((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };
    const videoContent = isCameraAvailable ? (_jsx(Grid, { item: true, container: true, sx: { backgroundColor: "action.disabledBackground" }, height: "100%", width: "100%", overflow: "hidden", borderRadius: 1, alignItems: "center", justifyContent: "center", children: _jsx(Webcam, { ref: cameraRef, height: "100%", width: "auto", audio: false, screenshotFormat: "image/jpeg", videoConstraints: { height: 800, width: 1200 } }) })) : (_jsxs(Grid, { item: true, container: true, sx: { backgroundColor: "action.disabledBackground" }, height: "100%", width: "100%", alignItems: "center", justifyContent: "center", direction: "column", rowGap: 1.5, children: [_jsx(Grid, { item: true, children: _jsx(NoPhotographySharpIcon, { sx: { fontSize: "100px", color: "action.disabled" } }) }), _jsx(Grid, { item: true, children: _jsx(H5, { color: "action.disabled", children: "No live feed available" }) })] }));
    const captureContent = (_jsxs(Grid, { container: true, position: "relative", width: "100%", height: "500px", children: [videoContent, _jsx(ImageCaptureOverlay, { children: _jsx(Grid, { container: true, height: "100%", sx: { margin: 0, padding: 1 }, direction: "column", alignItems: "center", rowSpacing: 1, children: isCameraAvailable ? (_jsxs(_Fragment, { children: [_jsxs(Grid, { item: true, container: true, flex: "1", children: [_jsx(Grid, { item: true, container: true, width: "11%", justifyContent: "center", alignItems: "center" }), _jsxs(Grid, { item: true, container: true, width: "78%", position: "relative", alignItems: "end", justifyContent: "center", p: 1, children: [_jsx(LivePhotoInstructions, { prompt: instructions }), _jsx(ImageCaptureCornerSvg, { position: IMAGE_CAPTURE_CORNER_POSITION.TOP_LEFT, size: 40 }), _jsx(ImageCaptureCornerSvg, { position: IMAGE_CAPTURE_CORNER_POSITION.TOP_RIGHT, size: 40 }), _jsx(ImageCaptureCornerSvg, { position: IMAGE_CAPTURE_CORNER_POSITION.BOTTOM_RIGHT, size: 40 }), _jsx(ImageCaptureCornerSvg, { position: IMAGE_CAPTURE_CORNER_POSITION.BOTTOM_LEFT, size: 40 })] }), _jsx(Grid, { item: true, container: true, width: "11%", justifyContent: "center", alignItems: "center" })] }), _jsxs(Grid, { item: true, container: true, flexDirection: "row", columnGap: 1, justifyContent: "center", alignItems: "center", children: [_jsx(Grid, { item: true, children: _jsx(Button, { leftIcon: _jsx(CameraSharpIcon, {}), variant: "contained", color: "primary", onClick: handleTakeImage, label: "Capture", size: "giant" }) }), _jsx(Grid, { item: true, children: _jsx(Button, { size: "giant", color: "error", variant: "contained", label: "Cancel", onClick: () => setIsTakingPhoto(false) }) })] })] })) : (_jsx(LivePhotoInstructions, { prompt: "To take a photo, enable camera access in your browser." })) }) })] }));
    const galleryContent = (_jsxs(Grid, { container: true, direction: "column", rowGap: 3, children: [_jsx(Grid, { item: true, children: _jsx(Box, { display: "flex", flexWrap: "wrap", gap: 1, children: issues.map((issue) => (_jsx(IssueChip, { label: issue.name, variant: "outlined", selected: selectedIssue === issue.code, onClick: () => setSelectedIssue(issue.code) }, issue.code))) }) }), selectedOption?.requiredPhotos && (_jsxs(Grid, { item: true, children: [_jsx(SubText, { color: "text.secondary", sx: { mb: 1.5 }, children: selectedOption.photoPrompt ?? "Photograph the item for evidence" }), _jsxs(Grid, { container: true, spacing: 1.5, children: [imageEntries.map(([key, url], idx) => (_jsx(Grid, { item: true, xs: 6, children: _jsx(PhotoCard, { index: idx, url: url ?? "", onImageDelete: () => handleRemoveImage(key) }) }, key))), imageEntries.length < maxPhotos && (_jsx(Grid, { item: true, xs: 6, children: _jsxs(PhotoPlaceholder, { onClick: () => setIsTakingPhoto(true), children: [_jsx(CameraSharpIcon, { sx: { fontSize: 36, color: "action.disabled", mb: 0.5 } }), _jsx(SubText, { color: "text.disabled", children: "Additional photo" })] }) }))] })] }))] }));
    const modalTitle = (_jsxs(Box, { display: "flex", alignItems: "center", gap: 1, children: [_jsx(WarningAmberIcon, { color: "error" }), _jsxs(Box, { children: [_jsx(Box, { fontWeight: 600, children: title }), _jsx(SubText, { color: "text.secondary", children: "Select the issue and take photos for evidence" })] })] }));
    return (_jsx(Modal, { title: modalTitle, isOpen: isOpen, content: isTakingPhoto ? captureContent : galleryContent, onClose: handleClose, width: "1200px", actions: isTakingPhoto
            ? []
            : [
                {
                    label: "Cancel",
                    variant: "text",
                    onClick: handleCancel,
                },
                {
                    label: "Save",
                    variant: "contained",
                    leftIcon: _jsx(SaveIcon, {}),
                    onClick: handleSubmit,
                    shortcutKey: "enter",
                    disabled: !selectedIssue ||
                        (selectedOption?.requiredPhotos === true &&
                            imageEntries.length === 0),
                },
            ] }));
};
export default IssueReportModal;
//# sourceMappingURL=index.js.map